// ============================================================
// Begehungs-Service — strukturierte Betriebsbegehung (§77 ASchG):
// Kopfdaten + Mängelliste (je Mangel: Beschreibung, Priorität,
// Maßnahme, Frist, Foto, Status). Daraus entstehen Protokoll-PDF
// und die Fristen-Nachverfolgung. Voll synchronisiert.
// ============================================================

import { db, type Begehung, type Mangel } from '../db/schema';
import { logAktion } from './auditLogService';
import type { MandantID } from '../types/branded';

export type { Begehung, Mangel };

export const PRIORITAET_META: Record<Mangel['prioritaet'], { label: string; cls: string; rank: number }> = {
  HOCH:    { label: 'Hoch',    cls: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400',       rank: 0 },
  MITTEL:  { label: 'Mittel',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400', rank: 1 },
  NIEDRIG: { label: 'Niedrig', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',        rank: 2 },
};

export function neueBegehung(mandantId: MandantID): Begehung {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    schema_version: 1,
    mandant_id: mandantId,
    datum: now.slice(0, 10),
    uhrzeit: '',
    uhrzeit_ende: '',
    ort: '',
    bereich: '',
    begleiter: '',
    allgemein: '',
    maengel: [],
    status: 'OFFEN',
    erstellt_am: now,
    geaendert_am: now,
  };
}

export function neuerMangel(): Mangel {
  return {
    id: crypto.randomUUID(),
    beschreibung: '',
    prioritaet: 'MITTEL',
    massnahme: '',
    erledigt: false,
  };
}

export async function loadBegehungen(mandantId?: string | null): Promise<Begehung[]> {
  const all = mandantId !== undefined && mandantId !== null && mandantId !== ''
    ? await db.begehungen.where('mandant_id').equals(mandantId).toArray()
    : await db.begehungen.toArray();
  return all.sort((a, b) => b.datum.localeCompare(a.datum));
}

export async function getBegehung(id: string): Promise<Begehung | undefined> {
  return db.begehungen.get(id);
}

export async function saveBegehung(b: Begehung): Promise<void> {
  const existing = await db.begehungen.get(b.id);
  await db.begehungen.put({ ...b, geaendert_am: new Date().toISOString() });
  void logAktion(b.mandant_id, existing !== undefined ? 'BEGEHUNG_BEARBEITET' : 'BEGEHUNG_ERSTELLT',
    { maengel: b.maengel.length });
}

export async function deleteBegehung(id: string): Promise<void> {
  await db.begehungen.delete(id);
  void logAktion('SYSTEM', 'BEGEHUNG_GELOESCHT');
}

// ── Offene Mängel / Fristen (Nachverfolgung) ───────────────────────────────

export interface OffenerMangel extends Mangel {
  begehung_id: string;
  mandant_id:  string;
  begehung_datum: string;
  bereich:     string;
  ueberfaellig: boolean;
}

/** Alle offenen (nicht erledigten) Mängel über alle Begehungen, nach Frist sortiert. */
export async function offeneMaengel(mandantId?: string | null): Promise<OffenerMangel[]> {
  const begehungen = await loadBegehungen(mandantId);
  const heute = new Date().toISOString().slice(0, 10);
  const offen: OffenerMangel[] = [];
  for (const b of begehungen) {
    for (const m of b.maengel) {
      if (m.erledigt) continue;
      offen.push({
        ...m,
        begehung_id: b.id,
        mandant_id: b.mandant_id,
        begehung_datum: b.datum,
        bereich: b.bereich,
        ueberfaellig: m.frist !== undefined && m.frist !== '' && m.frist < heute,
      });
    }
  }
  return offen.sort((a, b) => {
    // Überfällige zuerst, dann nach Frist, dann nach Priorität
    if (a.ueberfaellig !== b.ueberfaellig) return a.ueberfaellig ? -1 : 1;
    const fa = a.frist ?? '9999', fb = b.frist ?? '9999';
    if (fa !== fb) return fa.localeCompare(fb);
    return PRIORITAET_META[a.prioritaet].rank - PRIORITAET_META[b.prioritaet].rank;
  });
}

/** Einen Mangel (in seiner Begehung) als erledigt/offen umschalten. */
export async function toggleMangelErledigt(begehungId: string, mangelId: string): Promise<void> {
  const b = await db.begehungen.get(begehungId);
  if (b === undefined) return;
  const maengel = b.maengel.map(m => m.id === mangelId ? { ...m, erledigt: !m.erledigt } : m);
  await db.begehungen.put({ ...b, maengel, geaendert_am: new Date().toISOString() });
}
